import { BrowserTracing } from "@sentry/tracing"
import type { Breadcrumb, Event, Primitive, User } from "@sentry/types"
import * as Sentry from "@sentry/vue"

import { useUserStore } from "../stores/userStore"

// Recursion inside injected scripts (Google Translate) never touches our bundle
function isInjectedScriptOverflow(event: Event): boolean {
  const exception = event.exception?.values?.[0]
  if (exception?.type !== "RangeError" || !exception.value?.includes("call stack")) { return false }
  const frames = exception.stacktrace?.frames ?? []
  return frames.length > 0 && frames.every((frame) => !frame.filename?.includes("/_nuxt/"))
}

export default defineNuxtPlugin((nuxtApp) => {
  const { vueApp } = nuxtApp
  const environment = nuxtApp.$config.public.appEnv
  const enabled = ["production", "staging"].includes(environment)
  const dsn = "https://e5744fb53bb34d96bd9fddbecd13dcf2@o185386.ingest.sentry.io/4505040594731008"
  let unsampledClient: Sentry.BrowserClient | undefined

  const tagUser = (event: Event) => {
    if (isInjectedScriptOverflow(event)) { return null }
    const userStore = useUserStore()
    if (userStore.current?.id) {
      event.user = {
        ...event.user,
        id: String(userStore.current.id),
      }
      event.tags = {
        ...event.tags,
        user_id: String(userStore.current.id),
      }
    }
    if (userStore.authToken) {
      event.tags = {
        ...event.tags,
        token_prefix: userStore.authToken.substring(0, 6),
      }
    }
    return event
  }

  // We dont want Sentry to be initialized in development env,
  // because it catches errors still and throws in console
  // different stack trace and error message
  if (enabled) {
    Sentry.init({
      app: vueApp,
      debug: false,
      dsn,
      enabled,
      environment,
      integrations: [new BrowserTracing()],
      sampleRate: 0.2,
      maxBreadcrumbs: 50,
      tracesSampleRate: 0,
      ignoreErrors: [/ResizeObserver/i, /FetchError/i],
      trackComponents: false,
      beforeSend: tagUser,
    })

    Sentry.attachErrorHandler(vueApp, {
      logErrors: false,
      attachProps: true,
      trackComponents: true,
      timeout: 2000,
      hooks: ["activate", "create", "mount", "update"],
    })

    // Separate client with no sampling for rare events we must never miss
    unsampledClient = new Sentry.BrowserClient({
      dsn,
      environment,
      transport: Sentry.makeFetchTransport,
      stackParser: Sentry.defaultStackParser,
      integrations: [],
      beforeSend: tagUser,
    })
  }

  return {
    provide: {
      sentrySetContext: (n: string, context: { [key: string]: unknown }) => Sentry.setContext(n, context),
      sentrySetUser: (user: User) => Sentry.setUser(user),
      sentrySetTag: (tagName: string, value: Primitive) => Sentry.setTag(tagName, value),
      sentryAddBreadcrumb: (breadcrumb: Breadcrumb) => Sentry.addBreadcrumb(breadcrumb),
      sentryCaptureUnsampled: (message: string, tags: { [key: string]: Primitive }, extra: { [key: string]: unknown }) => {
        const scope = new Sentry.Scope()
        scope.setTags(tags)
        scope.setExtras(extra)
        unsampledClient?.captureMessage(message, "error", undefined, scope)
      },
    },
  }
})
